import React, { useMemo, useState, useEffect } from 'react';
import '../../style/css/loaddist2026.css';
import Icon from '../Icon';
import reservationsSvc from '../../services/reservations';
import managetables from '../../services/managetables';
import businessConfigSvc from '../../services/businessConfig';

/*
 * LoadDistribution — "חילוק עומסים" (day load-distribution) LIVE widget for the HOME dashboard.
 *
 * A histogram of the day's booking load across 15-minute slots. Each bar carries TWO numbers:
 *   (N)  — number of reservations / parties whose seating window overlaps that slot.
 *   Np   — number of covers / people (sum of party sizes) in that slot.
 * A reservation occupies MULTIPLE consecutive slots (its seating window = time..time+durationMin),
 * so every booking is counted in EVERY 15-min slot it overlaps — this is why load accumulates into
 * humps rather than single spikes, matching how a real dining room fills.
 *
 * The manager reads the shape (early rush / lull / prime peak) to decide when to reinforce staff.
 *
 * DATA: mock until the backend act exists. This wires later to a `loadDistribution` /
 * `reservationsList` act (see docs/BACKEND_ACTS_SPEC.md §3.1 / §3.11) whose reservations array is
 * shaped exactly like MOCK_RESERVATIONS below (each row → {time, party, durationMin, ...}).
 * Replace MOCK_RESERVATIONS with the act's `reservations` and the histogram recomputes as-is.
 */

// Today's reservations are loaded live (reservationsList) inside the component.

// Real "now" HH:MM (for the current-occupancy KPI).
const nowHHMM = () => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const SLOT_MIN = 15;
const DAY_START = '16:30';
const DAY_END = '23:30';

// ── helpers ────────────────────────────────────────────────────────────────
const toMin = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
};
const toHHMM = (min) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/*
 * bucketReservations — accumulate parties + covers into every 15-min slot a reservation overlaps.
 * Returns an ordered array of { slot:'HH:MM', startMin, parties, covers }.
 */
function bucketReservations(reservations, startHHMM, endHHMM, step = SLOT_MIN) {
    const start = toMin(startHHMM);
    const end = toMin(endHHMM);
    const slots = [];
    for (let t = start; t < end; t += step) {
        slots.push({ slot: toHHMM(t), startMin: t, parties: 0, covers: 0 });
    }
    reservations.forEach((r) => {
        const rStart = toMin(r.time);
        const rEnd = rStart + (r.durationMin || 90);
        slots.forEach((s) => {
            // overlap if the slot window [startMin, startMin+step) intersects [rStart, rEnd)
            if (rStart < s.startMin + step && rEnd > s.startMin) {
                s.parties += 1;
                s.covers += r.party || 0;
            }
        });
    });
    return slots;
}

const LoadDistribution = () => {
    const [active, setActive] = useState(null); // index of hovered/tapped bar (tooltip)
    const [reservations, setReservations] = useState([]); // today's real reservations
    const [capacity, setCapacity] = useState(0); // total seats (sum of table diners)

    // Load today's reservations (for the histogram: {time,party,durationMin}) + real seat
    // capacity from the tables. Duration isn't per-row in the DB → use the EFFECTIVE hold window
    // from business settings (seating duration + grace), matching the board/list/server.
    useEffect(() => {
        let alive = true;
        (async () => {
            const [{ rows }, tables, cfg] = await Promise.all([
                reservationsSvc.listToday([]),
                managetables.getAllTables(true).catch(() => []),
                businessConfigSvc.getConfig().catch(() => null),
            ]);
            if (!alive) return;
            const dur = businessConfigSvc.effectiveHold(cfg || {});
            setReservations((rows || []).map((r) => ({
                time: r.time ? String(r.time).slice(0, 5) : '',
                party: Number(r.party) || 0,
                durationMin: dur,
            })).filter((r) => r.time));
            const seats = (Array.isArray(tables) ? tables : [])
                .reduce((s, t) => s + (Number(t.dinersNumber || t.diners || t.seats) || 0), 0);
            setCapacity(seats);
        })();
        return () => { alive = false; };
    }, []);

    const slots = useMemo(
        () => bucketReservations(reservations, DAY_START, DAY_END),
        [reservations]
    );

    const maxCovers = useMemo(
        () => slots.reduce((m, s) => Math.max(m, s.covers), 0) || 1,
        [slots]
    );
    const peakCovers = maxCovers;

    // KPIs
    const totalParties = reservations.length;
    const totalCovers = reservations.reduce((sum, r) => sum + (r.party || 0), 0);
    const nowMin = toMin(nowHHMM());
    const nowSlot = slots.find((s) => nowMin >= s.startMin && nowMin < s.startMin + SLOT_MIN);
    const currentOccupancy = nowSlot ? nowSlot.covers : 0;

    const isPeak = (s) => s.covers === peakCovers && s.covers > 0;

    // Empty state: no bookings today → hide the histogram + legend (a wall of "(0) 0p"
    // columns is ugly and useless) and show a clean centered empty state. The KPI strip
    // stays (it reads a coherent 0 / 0 / 0/— / 0), so the widget never looks broken.
    const hasData = totalParties > 0;

    return (
        <section className="hp-section ld-section">
            {/* Header */}
            <div className="ld-head">
                <div className="ld-head-titles">
                    <div className="ld-title">
                        <Icon name="chart" size={22} className="ld-title-icon" />
                        <span>חילוק עומסים · היום</span>
                    </div>
                    <div className="ld-subtitle">
                        עומס ההזמנות לאורך היום — לפי חלונות של 15 דקות. כך יודעים מתי לחזק את הצוות.
                    </div>
                </div>
                {/* A per-service filter (צהריים/ערב) will come with real data + a dynamic
                    time axis; omitted now rather than shipping an inert control. */}
            </div>

            {/* COVERS KPI strip */}
            <div className="ld-kpis">
                <div className="ld-kpi">
                    <Icon name="calendar" size={18} className="ld-kpi-icon" />
                    <div className="ld-kpi-val">{totalParties}</div>
                    <div className="ld-kpi-label">הזמנות היום</div>
                </div>
                <div className="ld-kpi">
                    <Icon name="users" size={18} className="ld-kpi-icon" />
                    <div className="ld-kpi-val">{totalCovers}</div>
                    <div className="ld-kpi-label">סועדים</div>
                </div>
                <div className="ld-kpi">
                    <Icon name="clock" size={18} className="ld-kpi-icon" />
                    <div className="ld-kpi-val ld-kpi-val-frac">
                        {currentOccupancy}<span className="ld-kpi-den">/{capacity || '—'}</span>
                    </div>
                    <div className="ld-kpi-label">תפוסה נוכחית</div>
                </div>
                <div className="ld-kpi ld-kpi-peak">
                    <Icon name="chart" size={18} className="ld-kpi-icon" />
                    <div className="ld-kpi-val">{peakCovers}</div>
                    <div className="ld-kpi-label">שיא סועדים בו-זמנית</div>
                </div>
            </div>

            {!hasData ? (
                /* EMPTY STATE — no reservations today */
                <div className="ld-empty">
                    <div className="ld-empty-icon">
                        <Icon name="chart" size={28} />
                    </div>
                    <div className="ld-empty-title">אין עדיין הזמנות להיום</div>
                    <div className="ld-empty-text">
                        גרף העומס יתמלא כאן ברגע שתיכנס ההזמנה הראשונה — ותראו בדיוק מתי הערב מתחמם.
                    </div>
                </div>
            ) : (
              <>
            {/* Legend */}
            <div className="ld-legend">
                <span className="ld-legend-item"><b>(N)</b> = הזמנות</span>
                <span className="ld-legend-dot" aria-hidden="true">·</span>
                <span className="ld-legend-item"><b>Np</b> = סועדים</span>
                <span className="ld-legend-dot" aria-hidden="true">·</span>
                <span className="ld-legend-item ld-legend-peak">שיא</span>
            </div>

            {/* HISTOGRAM — scrolls INSIDE the card on mobile, never pushes the page */}
            <div className="ld-chart-scroll">
                <div className="ld-chart" role="img" aria-label="היסטוגרמת עומס הזמנות לפי שעה">
                    {/* horizontal gridlines */}
                    <div className="ld-gridlines" aria-hidden="true">
                        <span /><span /><span /><span />
                    </div>
                    <div className="ld-bars">
                        {slots.map((s, i) => {
                            const h = Math.round((s.covers / maxCovers) * 100);
                            const showLabel = s.startMin % 30 === 0; // thin the axis labels
                            const peak = isPeak(s);
                            return (
                                <div
                                    key={s.slot}
                                    className={`ld-bar-col${peak ? ' is-peak' : ''}${active === i ? ' is-active' : ''}`}
                                    onMouseEnter={() => setActive(i)}
                                    onMouseLeave={() => setActive((a) => (a === i ? null : a))}
                                    onClick={() => setActive((a) => (a === i ? null : i))}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setActive((a) => (a === i ? null : i));
                                        }
                                    }}
                                    aria-label={`${s.slot} — ${s.parties} הזמנות, ${s.covers} סועדים`}
                                >
                                    {active === i && s.covers > 0 && (
                                        <div className="ld-tooltip" role="tooltip">
                                            <div className="ld-tt-time">{s.slot}</div>
                                            <div className="ld-tt-row">
                                                <Icon name="calendar" size={13} /> {s.parties} הזמנות
                                            </div>
                                            <div className="ld-tt-row">
                                                <Icon name="users" size={13} /> {s.covers} סועדים
                                            </div>
                                        </div>
                                    )}
                                    <div className="ld-bar-nums">
                                        <span className="ld-bar-parties">({s.parties})</span>
                                        <span className="ld-bar-covers">{s.covers}p</span>
                                    </div>
                                    <div className="ld-bar-track">
                                        <div
                                            className="ld-bar-fill"
                                            style={{ height: `${Math.max(h, s.covers > 0 ? 6 : 0)}%` }}
                                        />
                                    </div>
                                    <div className={`ld-bar-time${showLabel ? '' : ' is-hidden'}`}>
                                        {showLabel ? s.slot : ''}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
              </>
            )}
        </section>
    );
};

export default LoadDistribution;
